import { apiFetch } from "@/lib/api";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Camera, ArrowLeft, ChevronDown, LogOut, Loader2,
  Trophy, XCircle, CheckCircle2, RotateCcw, Volume2, Sparkles
} from "lucide-react";


export default function QuizPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [quizDone, setQuizDone] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    fetchQuiz();
  }, []);

  const fetchQuiz = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch(`/api/quiz/generate`, {  });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to generate quiz");
      }
      const data = await response.json();
      setQuestions(data.questions);
      setCurrentQ(0);
      setScore(0);
      setQuizDone(false);
      setSelectedAnswer(null);
      setShowResult(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTTS = async (text, language) => {
    if (isPlaying) return;
    setIsPlaying(true);
    try {
      const response = await apiFetch(`/api/translate/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ text, voice: "nova", speed: 1.0, language }),
      });
      if (!response.ok) throw new Error();
      const data = await response.json();
      const audio = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
      audio.onended = () => setIsPlaying(false);
      audio.onerror = () => setIsPlaying(false);
      await audio.play();
    } catch {
      setIsPlaying(false);
    }
  };

  const handleAnswer = (option) => {
    if (showResult) return;
    setSelectedAnswer(option);
    setShowResult(true);
    if (option === questions[currentQ].correct_answer) {
      setScore((s) => s + 1);
    }
  };

  const nextQuestion = () => {
    if (currentQ + 1 >= questions.length) {
      setQuizDone(true);
    } else {
      setCurrentQ((q) => q + 1);
      setSelectedAnswer(null);
      setShowResult(false);
    }
  };

  const q = questions[currentQ];
  const progress = questions.length > 0 ? ((currentQ + (showResult ? 1 : 0)) / questions.length) * 100 : 0;
  const scorePercent = questions.length > 0 ? Math.round((score / questions.length) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Nav */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 flex items-center justify-between h-16">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/dashboard")}>
            <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center">
              <Camera className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-extrabold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>LingoLens</span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="rounded-full flex items-center gap-2 hover:bg-slate-50 px-2">
                <Avatar className="w-8 h-8">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-indigo-100 text-indigo-700 text-sm font-bold">{user?.name?.charAt(0)}</AvatarFallback>
                </Avatar>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 rounded-xl">
              <DropdownMenuItem className="text-sm text-slate-500 cursor-default">{user?.email}</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} className="text-red-600 cursor-pointer">
                <LogOut className="w-4 h-4 mr-2" />Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </nav>

      <main className="max-w-2xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" onClick={() => navigate("/dashboard")} className="rounded-full hover:bg-indigo-50" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>Vocabulary Quiz</h1>
            <p className="text-slate-500 mt-1">Test your translation knowledge</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 rounded-3xl bg-yellow-50 flex items-center justify-center mx-auto mb-6">
              <Sparkles className="w-10 h-10 text-yellow-400" />
            </div>
            <h3 className="text-xl font-bold text-slate-700 mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>Not enough vocabulary</h3>
            <p className="text-slate-400 mb-6">{error}</p>
            <Button onClick={() => navigate("/dashboard")} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-6 py-3 font-bold" data-testid="go-translate-btn">
              Translate More Images
            </Button>
          </div>
        ) : quizDone ? (
          /* Quiz Results */
          <div className="text-center py-12 animate-slide-up">
            <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 ${scorePercent >= 70 ? "bg-green-50" : scorePercent >= 40 ? "bg-yellow-50" : "bg-red-50"}`}>
              <Trophy className={`w-12 h-12 ${scorePercent >= 70 ? "text-green-500" : scorePercent >= 40 ? "text-yellow-500" : "text-red-400"}`} />
            </div>
            <h2 className="text-3xl font-black text-slate-900 mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              {scorePercent >= 70 ? "Amazing!" : scorePercent >= 40 ? "Good effort!" : "Keep practicing!"}
            </h2>
            <p className="text-5xl font-black text-indigo-600 mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              {score}/{questions.length}
            </p>
            <p className="text-slate-500 mb-8">
              {scorePercent >= 70 ? "You're mastering these translations!" : "Practice makes perfect. Try again!"}
            </p>
            <div className="flex gap-3 justify-center">
              <Button
                data-testid="retry-quiz-btn"
                onClick={fetchQuiz}
                className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-6 py-3 font-bold"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/flashcards")}
                className="rounded-full border-2 border-slate-200 px-6 py-3 font-bold"
              >
                Study Flashcards
              </Button>
            </div>
          </div>
        ) : q ? (
          /* Quiz Question */
          <div className="space-y-6">
            <Progress value={progress} className="h-2 rounded-full" />
            <p className="text-sm font-bold text-slate-400 text-center">
              Question {currentQ + 1} of {questions.length} — {q.language}
            </p>

            <div className="bg-white rounded-3xl border border-slate-100 shadow-xl shadow-indigo-100/20 p-8 text-center">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">What is this in {q.language}?</p>
              <p className="text-3xl sm:text-4xl font-black text-slate-900 capitalize" style={{ fontFamily: 'Nunito, sans-serif' }}>
                {q.original}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {q.options.map((option, i) => {
                let style = "bg-white border-2 border-slate-100 hover:border-indigo-300 hover:bg-indigo-50/30";
                if (showResult) {
                  if (option === q.correct_answer) {
                    style = "bg-green-50 border-2 border-green-400 text-green-800";
                  } else if (option === selectedAnswer && option !== q.correct_answer) {
                    style = "bg-red-50 border-2 border-red-400 text-red-800";
                  } else {
                    style = "bg-slate-50 border-2 border-slate-100 opacity-50";
                  }
                }

                return (
                  <button
                    key={i}
                    data-testid={`quiz-option-${i}`}
                    onClick={() => handleAnswer(option)}
                    disabled={showResult}
                    className={`${style} rounded-2xl p-4 text-left transition-all duration-200 flex items-center gap-3`}
                  >
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                      showResult && option === q.correct_answer ? "bg-green-200 text-green-800" :
                      showResult && option === selectedAnswer ? "bg-red-200 text-red-800" :
                      "bg-slate-100 text-slate-500"
                    }`}>
                      {String.fromCharCode(65 + i)}
                    </span>
                    <span className="font-semibold text-base capitalize">{option}</span>
                    {showResult && option === q.correct_answer && <CheckCircle2 className="w-5 h-5 text-green-500 ml-auto" />}
                    {showResult && option === selectedAnswer && option !== q.correct_answer && <XCircle className="w-5 h-5 text-red-500 ml-auto" />}
                  </button>
                );
              })}
            </div>

            {showResult && (
              <div className="flex items-center justify-between animate-slide-up">
                <Button
                  data-testid="listen-answer-btn"
                  variant="outline"
                  onClick={() => handleTTS(q.correct_answer, q.language)}
                  disabled={isPlaying}
                  className="rounded-full border-2 font-semibold"
                >
                  {isPlaying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Volume2 className="w-4 h-4 mr-2" />}
                  Listen
                </Button>
                <Button
                  data-testid="next-question-btn"
                  onClick={nextQuestion}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-6 font-bold"
                >
                  {currentQ + 1 >= questions.length ? "See Results" : "Next Question"}
                </Button>
              </div>
            )}
          </div>
        ) : null}
      </main>
    </div>
  );
}
